// @ts-nocheck
/**
 * DigiGuideLaunchLink — "Launch DigiGuide" button + breadcrumb nav.
 *
 * Single-row layout:
 *   [CTA <a>: [Row1: PhoneIcon {launchVerb} DCI DigiGuide] [Row2: {thisLabel} {contextNoun} →]]  [breadcrumb nav]
 *
 * Props:
 *   href              — dci-digiguide:/// deep link URL
 *   manualTitle       — manual title string
 *   logoUrl           — product logo image URL
 *   chapterTitle      — chapter title string (or null)
 *   topicTitle        — topic/article title string (or null)
 *   launchVerb        — translated "Launch" / "Open"
 *   manualLabel       — translated "Manual"
 *   chapterLabel      — translated "Chapter"
 *   articleLabel      — translated "Article"
 *   thisLabel         — translated "This"
 *   chapterId         — string or null (for anchor href construction)
 *   topicId           — string or null
 *   compact           — boolean, default false (true = header breadcrumb bar)
 *   isPreview         — boolean, default false
 *   isParked          — boolean, default false
 *   isProductionView  — boolean, default false
 *   family            — product family string (optional)
 *   variant           — product variant string (optional)
 *   style             — optional style overrides applied to the outer <div>
 */
import React from "react";
import dciLogo from "../images/DCI.png";

const font = "'Open Sans', system-ui, sans-serif";

const PhoneIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth="2" style={{ display: "block", flexShrink: 0 }}>
    <rect x="5" y="2" width="14" height="20" rx="2" ry="2" />
    <line x1="12" y1="18" x2="12.01" y2="18" />
  </svg>
);

const RightArrowIcon = ({ size = 14 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5"
    style={{ display: "block", flexShrink: 0 }}>
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const DciLogoBox = ({ size = 20 }) => (
  <span style={{
    display: "inline-flex", alignItems: "center", justifyContent: "center",
    width: size, height: size,
    background: "#0B83FF", borderRadius: "3px",
    flexShrink: 0,
  }}>
    <img src={dciLogo} alt="DCI" style={{ width: size * 0.7, height: size * 0.7, objectFit: "contain", display: "block" }} />
  </span>
);

export default function DigiGuideLaunchLink({
  href,
  manualTitle,
  logoUrl,
  chapterTitle,
  topicTitle,
  launchVerb = "Launch",
  manualLabel = "Manual",
  chapterLabel = "Chapter",
  articleLabel = "Article",
  thisLabel = "This",
  chapterId,
  topicId,
  compact = false,
  isPreview = false,
  isParked = false,
  isProductionView = false,
  family,
  variant,
  style,
}) {
  // Derive the context noun for the CTA label
  const contextNoun = topicTitle ? articleLabel : chapterTitle ? chapterLabel : manualLabel;

  // Status pill colors
  const pillIsGreen = isParked || isProductionView;
  const pillBg = pillIsGreen ? "#dcfce7" : "#fef3c7";
  const pillColor = pillIsGreen ? "#166534" : "#92400e";
  const pillLabel = isParked ? "PRODUCTION MODE" : isProductionView ? "PROD VIEW" : "LIVE PREVIEW";

  // Size-dependent styles driven by compact prop
  const outerStyle = compact
    ? { padding: "0", gap: "0.3rem", alignItems: "flex-start" }
    : { padding: "0.55rem 1rem", gap: "0.5rem", alignItems: "flex-start" };

  const ctaStyle = compact
    ? {
        fontSize: "0.68rem",
        fontWeight: 700,
        gap: "4px",
        padding: "0.15rem 0.4rem",
        borderRadius: "4px",
        background: "#e8f0fe",
        color: "#0055aa",
      }
    : {
        fontSize: "0.95rem",
        fontWeight: 700,
        gap: "6px",
        padding: "0.35rem 0.75rem",
        borderRadius: "6px",
        background: "#0066cc",
        color: "#fff",
      };

  const breadcrumbStyle = compact
    ? { fontSize: "0.68rem", color: "#666" }
    : { fontSize: "0.78rem", color: "#444" };

  const breadcrumbLinkColor = compact ? "#444" : "#0055aa";

  const iconSize = compact ? 10 : 14;
  const logoBoxSize = compact ? 14 : 18;

  // Anchor hrefs
  const chapterHref = chapterId ? `#cid-${chapterId}` : undefined;
  const topicHref = chapterId && topicId ? `#cid-${chapterId}-tid-${topicId}` : undefined;

  return (
    <div
      className="dgl-launch-link"
      style={{
        display: "flex",
        flexDirection: "row",
        alignItems: "flex-start",
        flexWrap: "wrap",
        fontFamily: font,
        lineHeight: 1.4,
        boxSizing: "border-box",
        ...outerStyle,
        ...style,
      }}
    >
      {/* Status pill — only when isPreview */}
      {isPreview && (
        <span style={{
          background: pillBg,
          color: pillColor,
          fontSize: "0.6rem",
          fontWeight: 700,
          padding: "1px 5px",
          borderRadius: "3px",
          flexShrink: 0,
          display: "inline-flex",
          alignItems: "center",
          gap: 3,
        }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: "currentColor", display: "inline-block" }} />
          {pillLabel}
        </span>
      )}

      {/* Right section — CTA link */}
      <a
        href={href}
        className="dgl-cta"
        style={{
          display: "inline-flex",
          flexDirection: "column",
          alignItems: "center",
          flexShrink: 0,
          textDecoration: "none",
          cursor: "pointer",
          WebkitTapHighlightColor: "transparent",
          textAlign: "center",
          ...ctaStyle,
        }}
      >
        {/* Row 1: Launch [DCI] DigiGuide */}
        <span style={{ display: "flex", alignItems: "center", gap: compact ? "3px" : "5px", justifyContent: "center" }}>
          <PhoneIcon size={iconSize} />
          {launchVerb}
          <DciLogoBox size={logoBoxSize} />
          DigiGuide
        </span>
        {/* Row 2: This [context] → */}
        <span style={{ display: "flex", alignItems: "center", gap: compact ? "3px" : "4px", justifyContent: "center" }}>
          {thisLabel} {contextNoun}
          <RightArrowIcon size={compact ? 10 : 13} />
        </span>
      </a>

      {/* Left section — breadcrumb nav */}
      <span
        className="dgl-breadcrumb"
        style={{
          display: "inline-flex",
          alignItems: "center",
          flexWrap: "wrap",
          gap: "0.2rem 0.25rem",
          minWidth: 0,
          marginLeft: "auto",
          flex: 1,
          ...breadcrumbStyle,
        }}
      >
        {/* Family */}
        {family && (
          <span style={{ opacity: 0.5, whiteSpace: "nowrap", fontSize: "0.9em" }}>{family}</span>
        )}

        {/* Logo */}
        {logoUrl && (
          <img
            src={logoUrl}
            alt=""
            loading="lazy"
            style={{
              maxHeight: compact ? "12px" : "14px",
              maxWidth: compact ? "48px" : "64px",
              objectFit: "contain",
              opacity: 0.85,
              flexShrink: 0,
              display: "block",
            }}
          />
        )}

        {/* Variant */}
        {variant && (
          <span style={{ opacity: 0.6, whiteSpace: "nowrap", fontSize: "0.9em", fontWeight: 600, color: "rgb(11,131,255)" }}>{variant}</span>
        )}

        {/* Manual title — only when no family/logo/variant to identify the product */}
        {manualTitle && !family && !logoUrl && !variant && (
          <span style={{ color: "inherit", opacity: 0.6, whiteSpace: "nowrap" }}>
            {manualTitle}
          </span>
        )}

        {/* Chapter breadcrumb */}
        {chapterTitle && (
          <>
            <span style={{ color: "#bbb", flexShrink: 0 }}>›</span>
            {chapterHref ? (
              <a
                href={chapterHref}
                style={{ color: breadcrumbLinkColor, textDecoration: "none", whiteSpace: "nowrap" }}
              >
                {chapterLabel}: {chapterTitle}
              </a>
            ) : (
              <span style={{ whiteSpace: "nowrap" }}>{chapterLabel}: {chapterTitle}</span>
            )}
          </>
        )}

        {/* Topic breadcrumb */}
        {topicTitle && (
          <>
            <span style={{ color: "#bbb", flexShrink: 0 }}>›</span>
            {topicHref ? (
              <a
                href={topicHref}
                style={{ color: breadcrumbLinkColor, textDecoration: "none", whiteSpace: "nowrap" }}
              >
                {articleLabel}: {topicTitle}
              </a>
            ) : (
              <span style={{ whiteSpace: "nowrap" }}>{articleLabel}: {topicTitle}</span>
            )}
          </>
        )}
      </span>
    </div>
  );
}
